import { tz } from "@date-fns/tz";
import { format, isValid, parseISO } from "date-fns";

/**
 * Formats a date string or Date object as MM/DD/YYYY (US convention).
 *
 * `parseISO` only understands ISO-8601 input. The patient DOB returned by the
 * API (and used throughout the UI) is in MM/DD/YYYY form, which `parseISO`
 * cannot parse — it returns an Invalid Date, and `format` then throws
 * `RangeError: Invalid time value`. To avoid crashing the caller we guard
 * against unparseable input and return the original string unchanged.
 *
 * Pass the clinic's IANA `timeZone` to render an instant on the clinic's
 * clock rather than the browser's. A missing or unknown zone falls back to the
 * browser's.
 */
export function formatDate(
  date: string | Date,
  formatString = "MM-dd-yyyy",
  timeZone?: string | null,
): string {
  const d = typeof date === "string" ? parseISO(date) : date;
  if (!isValid(d)) {
    return typeof date === "string" ? date : "";
  }
  if (timeZone) {
    try {
      return format(d, formatString, { in: tz(timeZone) });
    } catch {
      // An unknown IANA zone throws a RangeError; fall back to the browser's zone.
    }
  }
  return format(d, formatString);
}

/**
 * Returns the current local time as a 12-hour string, e.g. "9:30 AM".
 */
export function nowTimeStr(): string {
  const d = new Date();
  const h = d.getHours();
  const m = d.getMinutes();
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${m.toString().padStart(2, "0")} ${ampm}`;
}

/**
 * Converts a 12-hour time string (e.g. "9:30 AM") to minutes since midnight.
 * Returns 0 for unrecognised input.
 */
export function parseTime(t: string): number {
  const match = t.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) return 0;
  let h = parseInt(match[1], 10);
  const mm = parseInt(match[2], 10);
  const p = match[3].toUpperCase();
  if (p === "PM" && h !== 12) h += 12;
  if (p === "AM" && h === 12) h = 0;
  return h * 60 + mm;
}
